import { BaseSocialLogin } from './base';
export class GoogleSocialLogin extends BaseSocialLogin {
    constructor() {
        super(...arguments);
        this.clientId = null;
        this.loginType = 'online';
        this.GOOGLE_TOKEN_REQUEST_URL = 'https://www.googleapis.com/oauth2/v3/tokeninfo';
        this.GOOGLE_STATE_KEY = 'capgo_social_login_google_state';
    }
    async initialize(clientId, mode, hostedDomain, redirectUrl) {
        this.clientId = clientId;
        if (mode) {
            this.loginType = mode;
        }
        this.hostedDomain = hostedDomain;
        this.redirectUrl = redirectUrl;
    }
    async login(options) {
        if (!this.clientId) {
            throw new Error('Google Client ID not set. Call initialize() first.');
        }
        let scopes = options.scopes || [];
        if (scopes.length > 0) {
            // If scopes are provided, directly use the traditional OAuth flow
            if (!scopes.includes('https://www.googleapis.com/auth/userinfo.email')) {
                scopes.push('https://www.googleapis.com/auth/userinfo.email');
            }
            if (!scopes.includes('https://www.googleapis.com/auth/userinfo.profile')) {
                scopes.push('https://www.googleapis.com/auth/userinfo.profile');
            }
            if (!scopes.includes('openid')) {
                scopes.push('openid');
            }
        }
        else {
            scopes = [
                'https://www.googleapis.com/auth/userinfo.email',
                'https://www.googleapis.com/auth/userinfo.profile',
                'openid',
            ];
        }
        const nonce = options.nonce || Math.random().toString(36).substring(2);
        // If scopes are provided, directly use the traditional OAuth flow
        return this.traditionalOAuth({
            scopes,
            nonce,
            hostedDomain: this.hostedDomain,
            prompt: options.prompt,
        });
    }
    async logout() {
        if (this.loginType === 'offline') {
            return Promise.reject("Offline login doesn't store tokens. logout is not available");
        }
        // eslint-disable-next-line
        const state = this.getGoogleState();
        if (!state)
            return;
        await this.rawLogoutGoogle(state.accessToken);
    }
    async isLoggedIn() {
        if (this.loginType === 'offline') {
            return Promise.reject("Offline login doesn't store tokens. isLoggedIn is not available");
        }
        // eslint-disable-next-line
        const state = this.getGoogleState();
        if (!state)
            return { isLoggedIn: false };
        try {
            const isValidAccessToken = await this.accessTokenIsValid(state.accessToken);
            const isValidIdToken = this.idTokenValid(state.idToken);
            if (isValidAccessToken && isValidIdToken) {
                return { isLoggedIn: true };
            }
            else {
                try {
                    await this.rawLogoutGoogle(state.accessToken, false);
                }
                catch (e) {
                    console.error('Access token is not valid, but cannot logout', e);
                }
                return { isLoggedIn: false };
            }
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
    async getAuthorizationCode() {
        if (this.loginType === 'offline') {
            return Promise.reject("Offline login doesn't store tokens. getAuthorizationCode is not available");
        }
        // eslint-disable-next-line
        const state = this.getGoogleState();
        if (!state)
            throw new Error('No Google authorization code available');
        try {
            const isValidAccessToken = await this.accessTokenIsValid(state.accessToken);
            const isValidIdToken = this.idTokenValid(state.idToken);
            if (isValidAccessToken && isValidIdToken) {
                return { accessToken: state.accessToken, jwt: state.idToken };
            }
            else {
                try {
                    await this.rawLogoutGoogle(state.accessToken, false);
                }
                catch (e) {
                    console.error('Access token is not valid, but cannot logout', e);
                }
                throw new Error('No Google authorization code available');
            }
        }
        catch (e) {
            return Promise.reject(e);
        }
    }
    async refresh() {
        // For Google, we can prompt for re-authentication
        return Promise.reject('Not implemented');
    }
    handleOAuthRedirect(url) {
        const paramsRaw = url.searchParams;
        // Check for errors in search params first (for offline mode)
        const errorInParams = paramsRaw.get('error');
        if (errorInParams) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            const errorDescription = paramsRaw.get('error_description') || errorInParams;
            return { error: errorDescription };
        }
        const code = paramsRaw.get('code');
        if (code && paramsRaw.has('scope')) {
            return {
                provider: 'google',
                result: {
                    serverAuthCode: code,
                    responseType: 'offline',
                },
            };
        }
        const hash = url.hash.substring(1);
        console.log('handleOAuthRedirect', url.hash);
        if (!hash)
            return null;
        const params = new URLSearchParams(hash);
        // Check for error cases in hash (e.g., user cancelled)
        const error = params.get('error');
        if (error) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            const errorDescription = params.get('error_description') || error;
            return { error: errorDescription };
        }
        console.log('handleOAuthRedirect ok');
        const accessToken = params.get('access_token');
        const idToken = params.get('id_token');
        if (accessToken && idToken) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            const profile = this.parseJwt(idToken);
            return {
                provider: 'google',
                result: {
                    accessToken: {
                        token: accessToken,
                    },
                    idToken,
                    profile: {
                        email: profile.email || null,
                        familyName: profile.family_name || null,
                        givenName: profile.given_name || null,
                        id: profile.sub || null,
                        name: profile.name || null,
                        imageUrl: profile.picture || null,
                    },
                    responseType: 'online',
                },
            };
        }
        return null;
    }
    async accessTokenIsValid(accessToken) {
        const url = `${this.GOOGLE_TOKEN_REQUEST_URL}?access_token=${encodeURIComponent(accessToken)}`;
        try {
            // Make the GET request using fetch
            const response = await fetch(url);
            // Check if the response is successful
            if (!response.ok) {
                console.log(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response not successful. Status code: ${response.status}. Assuming that the token is not valid`);
                return false;
            }
            // Get the response body as text
            const responseBody = await response.text();
            if (!responseBody) {
                console.error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response body is null`);
                throw new Error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response body is null`);
            }
            // Parse the response body as JSON
            let jsonObject;
            try {
                jsonObject = JSON.parse(responseBody);
            }
            catch (e) {
                console.error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response body is not valid JSON. Error: ${e}`);
                throw new Error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response body is not valid JSON. Error: ${e}`);
            }
            // Extract the 'expires_in' field
            const expiresInStr = jsonObject['expires_in'];
            if (expiresInStr === undefined || expiresInStr === null) {
                console.error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response JSON does not include 'expires_in'.`);
                throw new Error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. Response JSON does not include 'expires_in'.`);
            }
            // Parse 'expires_in' as an integer
            let expiresInInt;
            try {
                expiresInInt = parseInt(expiresInStr, 10);
                if (isNaN(expiresInInt)) {
                    throw new Error(`'expires_in' is not a valid integer`);
                }
            }
            catch (e) {
                console.error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. 'expires_in': ${expiresInStr} is not a valid integer. Error: ${e}`);
                throw new Error(`Invalid response from ${this.GOOGLE_TOKEN_REQUEST_URL}. 'expires_in': ${expiresInStr} is not a valid integer. Error: ${e}`);
            }
            // Determine if the access token is valid based on 'expires_in'
            return expiresInInt > 5;
        }
        catch (error) {
            console.error(error);
            throw error;
        }
    }
    idTokenValid(idToken) {
        try {
            const parsed = this.parseJwt(idToken);
            const currentTime = Math.ceil(Date.now() / 1000) + 5; // Convert current time to seconds since epoch
            return parsed.exp && currentTime < parsed.exp;
        }
        catch (e) {
            return false;
        }
    }
    async rawLogoutGoogle(accessToken, tokenValid = null) {
        if (tokenValid === null) {
            tokenValid = await this.accessTokenIsValid(accessToken);
        }
        if (tokenValid === true) {
            try {
                await fetch(`https://accounts.google.com/o/oauth2/revoke?token=${encodeURIComponent(accessToken)}`);
                this.clearStateGoogle();
            }
            catch (e) {
                // ignore
            }
            return;
        }
        else {
            this.clearStateGoogle();
            return;
        }
    }
    persistStateGoogle(accessToken, idToken) {
        try {
            window.localStorage.setItem(this.GOOGLE_STATE_KEY, JSON.stringify({ accessToken, idToken }));
        }
        catch (e) {
            console.error('Cannot persist state google', e);
        }
    }
    clearStateGoogle() {
        try {
            window.localStorage.removeItem(this.GOOGLE_STATE_KEY);
        }
        catch (e) {
            console.error('Cannot clear state google', e);
        }
    }
    getGoogleState() {
        try {
            const state = window.localStorage.getItem(this.GOOGLE_STATE_KEY);
            if (!state)
                return null;
            const { accessToken, idToken } = JSON.parse(state);
            return { accessToken, idToken };
        }
        catch (e) {
            console.error('Cannot get state google', e);
            return null;
        }
    }
    async traditionalOAuth({ scopes, hostedDomain, nonce, prompt, }) {
        var _a;
        const uniqueScopes = [...new Set([...(scopes || []), 'openid'])];
        const params = new URLSearchParams(Object.assign(Object.assign({ client_id: (_a = this.clientId) !== null && _a !== void 0 ? _a : '', redirect_uri: this.redirectUrl || window.location.origin + window.location.pathname, response_type: this.loginType === 'offline' ? 'code' : 'token id_token', scope: uniqueScopes.join(' ') }, (nonce && { nonce })), { include_granted_scopes: 'true', state: 'popup' }));
        if (hostedDomain !== undefined) {
            params.append('hd', hostedDomain);
        }
        if (prompt !== undefined) {
            params.append('prompt', prompt);
        }
        const url = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        localStorage.setItem(BaseSocialLogin.OAUTH_STATE_KEY, JSON.stringify({ provider: 'google', loginType: this.loginType, nonce }));
        const popup = window.open(url, 'Google Sign In', `width=${width},height=${height},left=${left},top=${top},popup=1`);
        let popupClosedInterval;
        let timeoutHandle;
        // Use BroadcastChannel for cross-origin communication (works when postMessage doesn't)
        const channelName = `google_oauth_${nonce || Date.now()}`;
        let broadcastChannel = null;
        try {
            broadcastChannel = new BroadcastChannel(channelName);
        }
        catch (_b) {
            // BroadcastChannel not supported, fall back to postMessage only
        }
        // This may never return...
        return new Promise((resolve, reject) => {
            if (!popup) {
                reject(new Error('Failed to open popup'));
                return;
            }
            const cleanup = () => {
                window.removeEventListener('message', handleMessage);
                clearInterval(popupClosedInterval);
                clearTimeout(timeoutHandle);
                if (broadcastChannel) {
                    broadcastChannel.close();
                }
            };
            const processOAuthResponse = (data) => {
                if (this.loginType === 'online') {
                    const { accessToken, idToken } = data;
                    if (accessToken && idToken) {
                        const profile = this.parseJwt(idToken);
                        this.persistStateGoogle(accessToken.token, idToken);
                        resolve({
                            provider: 'google',
                            result: {
                                accessToken: {
                                    token: accessToken.token,
                                },
                                idToken,
                                profile: {
                                    email: profile.email || null,
                                    familyName: profile.family_name || null,
                                    givenName: profile.given_name || null,
                                    id: profile.sub || null,
                                    name: profile.name || null,
                                    imageUrl: profile.picture || null,
                                },
                                responseType: 'online',
                            },
                        });
                    }
                    else {
                        reject(new Error('Invalid OAuth response: missing accessToken or idToken'));
                    }
                }
                else {
                    const { serverAuthCode } = data;
                    if (!serverAuthCode) {
                        reject(new Error('Invalid OAuth response: missing serverAuthCode'));
                        return;
                    }
                    resolve({
                        provider: 'google',
                        result: {
                            responseType: 'offline',
                            serverAuthCode,
                        },
                    });
                }
            };
            const handleMessage = (event) => {
                var _a, _b, _c, _d;
                if (event.origin !== window.location.origin || ((_b = (_a = event.data) === null || _a === void 0 ? void 0 : _a.source) === null || _b === void 0 ? void 0 : _b.startsWith('angular')))
                    return;
                if (((_c = event.data) === null || _c === void 0 ? void 0 : _c.type) === 'oauth-response') {
                    cleanup();
                    processOAuthResponse(event.data);
                }
                else if (((_d = event.data) === null || _d === void 0 ? void 0 : _d.type) === 'oauth-error') {
                    cleanup();
                    const errorMessage = event.data.error || 'User cancelled the OAuth flow';
                    reject(new Error(errorMessage));
                }
                // Don't reject for non-OAuth messages, just ignore them
            };
            // Listen for BroadcastChannel messages
            if (broadcastChannel) {
                broadcastChannel.onmessage = (event) => {
                    var _a;
                    const data = event.data;
                    if ((_a = data === null || data === void 0 ? void 0 : data.source) === null || _a === void 0 ? void 0 : _a.toString().startsWith('angular'))
                        return;
                    if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-response') {
                        cleanup();
                        processOAuthResponse(data);
                    }
                    else if ((data === null || data === void 0 ? void 0 : data.type) === 'oauth-error') {
                        cleanup();
                        const errorMessage = data.error || 'User cancelled the OAuth flow';
                        reject(new Error(errorMessage));
                    }
                };
            }
            window.addEventListener('message', handleMessage);
            // Timeout after 5 minutes
            timeoutHandle = setTimeout(() => {
                cleanup();
                try {
                    popup.close();
                }
                catch (_a) {
                    // Ignore cross-origin errors when closing
                }
                reject(new Error('OAuth timeout'));
            }, 300000);
            popupClosedInterval = setInterval(() => {
                try {
                    // Check if popup is closed - this may throw cross-origin errors for some providers
                    if (popup.closed) {
                        cleanup();
                        reject(new Error('Popup closed'));
                    }
                }
                catch (_a) {
                    // Cross-origin error when checking popup.closed - this happens when the popup
                    // navigates to a third-party OAuth provider with strict security settings.
                    // We can't detect if the window was closed, so we rely on timeout and message handlers.
                    clearInterval(popupClosedInterval);
                }
            }, 1000);
        });
    }
}
//# sourceMappingURL=google-provider.js.map